import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { toast } from '../../../../../../ui/toast';
import { Button, MainLayout } from '../../../../../../ui';
import { ArrowLeft } from '../../../../../../ui/icons/icons';
import { getAssessment, getDetailedAssessmentResults, resultsPreScreen } from '../../../../../../utils/api_service/assessment_api';
import { getBEIResults } from '../../../../../../utils/api_service/Behavioral';
import {
  Assessment,
  AssessmentQuestionResult,
  AttemptSkill,
  User,
} from '../../../../../../utils/types/assessment_types';
import { Tabs } from '../../../../../assessment/view/tabs/tabs';
import { BEIResultsData } from '../../../../../assessment/view/view_assessment/assessment_result_list/Containers';
import {
  DetailedResults,
  PreScreenResults,
  Summary,
} from '../../../../../assessment/view/view_assessment/assessment_result_list/Containers/AssessmentResultDetails/Containers';

interface AutoAssessmentResultsDetailsProps {
  assessmentId?: string;
  userId?: string;
  id?: string;
  backToClick?: () => void;
}

export const AutoAssessmentResultsDetails: React.FC<AutoAssessmentResultsDetailsProps> = ({
  assessmentId: propAssessmentId,
  userId: propUserId,
  id: propId,
  backToClick,
}) => {
  const RESULTS_TABS = ['Summary', 'Detailed Results', 'Pre-Screen Results'];
  const [activeTab, setActiveTab] = useState<number>(0);
  const [beiResults, setBeiResults] = useState<BEIResultsData[] | null>(null);
  const [assessmentData, setAssessmentData] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [, setSkills] = useState<AttemptSkill[]>();
  const [, setDetailedQuestions] = useState<AssessmentQuestionResult[]>([]);
  const [, setStoredQuestions] = useState<AssessmentQuestionResult[]>([]);
  const params =
    useParams<{ assessmentId: string; userId: string; id: string }>();

  // Use props if provided (embedded mode), otherwise use URL params
  const assessmentId = propAssessmentId || params.assessmentId;
  const userId = propUserId || params.userId;
  const id = propId || params.id;

  // Extract ID from URL path as fallback
  const getIdFromUrl = () => {
    const pathParts = window.location.pathname.split('/');
    // URL structure: /view-assessment-result/assessmentId/userId/id
    return pathParts[4] || id; // 4th index for the id parameter
  };

  const finalId = id || getIdFromUrl();
  const [, setUser] = useState<User>();
  const [, setAssessmentDetails] = useState<Assessment>();
  const [preScreenResults, setPreScreenResults] = useState<any[]>([]);

  useEffect(() => {
    // Fetch detailed assessment results with proper error handling
    const fetchAssessmentData = async () => {
      if (!finalId) {
        setError('Assessment result ID not found in URL');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await getDetailedAssessmentResults(finalId);

        if (response && response.questions && response.questions.length > 0) {
          setSkills(response.skills);
          let newDetailedQuestions: AssessmentQuestionResult[] = [];
          let newStoredQuestions: AssessmentQuestionResult[] = [];

          response.questions.forEach((result: AssessmentQuestionResult) => {
            let questionResult = result;
            let startTime: number = 0;
            let endTime: number = 0;
            if (questionResult.timeStarted && questionResult.timeCompleted) {
              startTime =
                new Date(
                  questionResult.timeStarted.toString().split(' ').join('T'),
                ).getTime() / 1000;
              endTime =
                new Date(
                  questionResult.timeCompleted.toString().split(' ').join('T'),
                ).getTime() / 1000;
            }
            questionResult.completionTime = endTime - startTime;
            newDetailedQuestions.push(questionResult);
            newStoredQuestions.push(questionResult);
          });

          setDetailedQuestions(newDetailedQuestions);
          setStoredQuestions(newStoredQuestions);
          setUser(response.user);

          // Fetch assessment details
          try {
            const assessmentDetailsResponse = await getAssessment(
              response.questions[0].assessmentId,
            );
            setAssessmentDetails(assessmentDetailsResponse);
          } catch (assessmentError) {
            console.warn(
              'Failed to fetch assessment details:',
              assessmentError,
            );
          }

          // Set the new assessment data format for DetailedResults
          setAssessmentData(response);
        } else {
          setAssessmentData(null);
        }
      } catch {
        setAssessmentData(null);
        toast.error(
          'Failed to load assessment data. Some features may not be available.',
          {
            position: 'bottom-center',
          },
        );
      } finally {
        setLoading(false);
      }
    };

    // Fetch pre-screen results with proper error handling
    const fetchPreScreenData = async () => {
      if (!finalId) {
        setPreScreenResults([]);
        return;
      }

      try {
        const response = await resultsPreScreen(finalId);
        if (!response.ok) {
          throw new Error(
            `Network response was not ok: ${response.statusText}`,
          );
        }
        const data = await response.json();
        setPreScreenResults(data);
      } catch {
        // Don't show toast error for pre-screen since it's not critical
        setPreScreenResults([]);
      }
    };

    fetchAssessmentData();
    fetchPreScreenData();
  }, [finalId]);

  useEffect(() => {
    const fetchBEIResults = async () => {
      // Manual extraction from URL as fallback
      const pathParts = window.location.pathname.split('/');
      const manualAssessmentId = pathParts[2]; // /view-assessment-result/[assessmentId]/[userId]/[id]
      const manualUserId = pathParts[3];

      // Use manual extraction if useParams fails
      const finalAssessmentId = assessmentId || manualAssessmentId;
      const finalUserId = userId || manualUserId;

      if (!finalAssessmentId || !finalUserId) {
        setError('Assessment ID or User ID not found in URL');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await getBEIResults(finalAssessmentId, finalUserId);
        if (response.ok) {
          const data = await response.json();
          setBeiResults(data);
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch BEI results');
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchBEIResults();
  }, [assessmentId, userId]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Summary
              beiResults={beiResults}
              assessmentData={assessmentData}
              loading={loading}
              error={error}
            />
          </Box>
        );
      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <DetailedResults
              beiResults={beiResults || []}
              assessmentData={assessmentData}
              loading={loading}
              error={error}
            />
          </Box>
        );
      case 2:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <PreScreenResults
              preScreenResults={preScreenResults}
              loading={loading}
              error={error}
            />
          </Box>
        );
      default:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Summary
              beiResults={beiResults}
              assessmentData={assessmentData}
              loading={loading}
              error={error}
            />
          </Box>
        );
    }
  };

  const content = (
    <>
      <Tabs
        tabTitles={RESULTS_TABS}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      {renderTabContent()}
    </>
  );

  return (
    <Box>
      <MainLayout>
        <Box sx={{ display: 'flex', mb: 2 }}>
          <Button
            hasIcon
            icon={<ArrowLeft size={16} />}
            onClick={backToClick}
            label="Back to Results List"
            variant="secondary"
          />
        </Box>
        {content}
      </MainLayout>
    </Box>
  );
};

export default AutoAssessmentResultsDetails;
